import * as React from 'react'
import { ArticleBusinessType, InventoryPool } from 'admin/articleService'
import { useGetAllArticles, useGetAllCategories } from 'products/redux'
import { useGetTimeSlotGroupsQuery } from 'timeSlots/reduxQuery'
import { useGetCapacitiesQuery } from 'capacity/reduxQuery'
import { useTimeSlotGroupDetails } from 'capacity/hooks/useTimeSlotGroupDetails'
import { capacityPaths } from 'capacity/paths'
import { FORM_SECTION } from 'capacity/copy'
import { CapacitySectionShell } from './capacitySectionShell'
import { CapacityRow, LinesRow, MessageRow, PendingRow } from './capacitySectionRow'
import { TimeSlotGroupCard } from './timeSlotGroupCard'
import { articleCategoryIds, categoriesAllSwitchedOff, productCapacityRows, productGroupBlocks } from './sectionModel'

interface ProductCapacitySectionProps {
    accountSlug: string
    productId: string
    businessType: ArticleBusinessType | null
    usedResources: InventoryPool[]
    // True while the form is still fetching the product: usedResources is empty until then.
    loading?: boolean
}

export function ProductCapacitySection({
    accountSlug,
    productId,
    businessType,
    usedResources,
    loading = false,
}: ProductCapacitySectionProps) {
    const isNew = productId === 'new'
    const articles = useGetAllArticles()
    const categories = useGetAllCategories()
    const capacities = useGetCapacitiesQuery(accountSlug, { skip: isNew })
    const groupsQuery = useGetTimeSlotGroupsQuery(accountSlug, { skip: isNew })

    const article = React.useMemo(() => articles.find((entry) => entry.id === productId), [articles, productId])
    const isBundle = !!article?.isBundle || businessType === 'bundle'
    const categoryIds = React.useMemo(() => (article ? articleCategoryIds(article) : []), [article])
    const groups = groupsQuery.data?.groups
    const resolved = React.useMemo(
        () => (groups ? productGroupBlocks({ id: productId, categoryIds }, groups, categories) : null),
        [groups, productId, categoryIds, categories],
    )
    const neededIds = React.useMemo(
        () =>
            groupsQuery.data && !groupsQuery.data.slotsIncluded && resolved
                ? resolved.blocks.flatMap((b) => b.groups.map((g) => g.id))
                : [],
        [groupsQuery.data, resolved],
    )
    const details = useTimeSlotGroupDetails(accountSlug, neededIds)

    const window = resolved ? (resolved.blocks.length > 0 ? 'slot' : 'day') : null
    const rows = productCapacityRows({
        accountSlug,
        productId,
        businessType,
        isBundle,
        usedResources,
        capacities: capacities.data,
        categories,
        window,
    })

    let capacityContent: React.ReactNode
    if (isNew) {
        capacityContent = <MessageRow text={FORM_SECTION.saveProductFirst} />
    } else if (loading || capacities.isLoading) {
        capacityContent = <PendingRow />
    } else if (capacities.isError) {
        capacityContent = <MessageRow text={FORM_SECTION.capacitiesFailed} />
    } else if (rows.length === 0) {
        capacityContent = <MessageRow text={FORM_SECTION.notInAny} />
    } else {
        capacityContent = rows.map((row) => (
            <CapacityRow key={row.key} href={row.href} name={row.name} detail={row.detail} pending={row.pending} />
        ))
    }

    let timeSlotsContent: React.ReactNode
    if (isNew) {
        timeSlotsContent = <MessageRow text={FORM_SECTION.saveProductFirstTimeSlots} />
    } else if (isBundle) {
        timeSlotsContent = <MessageRow text={FORM_SECTION.bundleTimeSlots} />
    } else if (groupsQuery.isError) {
        timeSlotsContent = <MessageRow text={FORM_SECTION.groupsFailed} />
    } else if (!resolved || !groups) {
        timeSlotsContent = <PendingRow />
    } else if (resolved.blocks.length === 0) {
        timeSlotsContent = (
            <LinesRow
                lines={[
                    FORM_SECTION.noTimeSlots,
                    categoriesAllSwitchedOff(categoryIds, categories)
                        ? FORM_SECTION.switchedOff
                        : FORM_SECTION.noGroupProduct,
                ]}
                linkTo={capacityPaths.timeSlots(accountSlug)}
                linkLabel={FORM_SECTION.goToTimeSlots}
            />
        )
    } else {
        timeSlotsContent = (
            <div className="capacity-section-blocks">
                {resolved.dependsOnCategory && <p className="capacity-section-lead">{FORM_SECTION.viaCategory}</p>}
                {resolved.blocks.map((block) => (
                    <div className="capacity-section-block" key={block.label}>
                        <div className="capacity-section-block-label">{block.label}</div>
                        {block.groups.map((group) => (
                            <TimeSlotGroupCard
                                key={group.id}
                                accountSlug={accountSlug}
                                group={group}
                                detail={details[group.id]}
                            />
                        ))}
                    </div>
                ))}
            </div>
        )
    }

    return <CapacitySectionShell capacities={capacityContent} timeSlots={timeSlotsContent} />
}
